import React, { useEffect, useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import {
  LOOKUP_LABEL, REFUSAL_WINDOWS, RefusalCounts, RefusalSummary, activeDays, refusalHeadline, totalOf,
} from './refusalCountRules';

const card = 'bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4';
const h2 = 'text-lg font-semibold text-gray-900 dark:text-white';
const muted = 'text-sm text-gray-500 dark:text-gray-400';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap text-left';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const tdNum = `${td} text-right tabular-nums`;

const Stat: React.FC<{ label: string; value: number; hint: string }> = ({ label, value, hint }) => (
  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
    <div className="text-xs uppercase text-gray-500 dark:text-gray-400">{label}</div>
    <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{value}</div>
    <div className="text-xs text-gray-500 dark:text-gray-400">{hint}</div>
  </div>
);

const CountCells: React.FC<{ c: RefusalCounts }> = ({ c }) => (
  <>
    <td className={tdNum}>{c.refused}</td>
    <td className={tdNum}>{c.wouldRefuse}</td>
    <td className={tdNum}>{c.capped + c.wouldCap}</td>
  </>
);

const CountHead: React.FC<{ first: string }> = ({ first }) => (
  <thead>
    <tr>
      <th className={th}>{first}</th>
      <th className={`${th} text-right`}>Refused</th>
      <th className={`${th} text-right`}>Would refuse</th>
      <th className={`${th} text-right`}>Capped</th>
    </tr>
  </thead>
);

/**
 * Inbound calls the tata switches refused, would have refused, or capped, for this organisation
 * (and those beneath it), over the last 7 or 30 IST days. The server decides whose rows the caller
 * sees. Shown only when the build sets REACT_APP_ENABLE_FEATURES=refusal-counts (MyBilling).
 */
const RefusalCountsPanel: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const [days, setDays] = useState<number>(7);
  const [data, setData] = useState<RefusalSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    billingService.getMyRefusals({ days, organizationId }).then((r) => {
      if (!live) return;
      if (r.success && r.data) setData(r.data);
      else {
        setData(null);
        setError(r.error || 'Failed to load refused calls');
      }
    });
    return () => {
      live = false;
    };
  }, [days, organizationId]);

  const t = data?.totals;
  const daysShown = data ? activeDays(data) : [];
  const orgs = (data?.byOrganization || []).filter((o) => totalOf(o.counts) > 0);

  return (
    <section className={card} aria-label="Refused calls">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={h2}>Refused calls</h2>
          <p className={muted}>{data ? refusalHeadline(data) : 'Inbound calls stopped or shortened by the switch, by day (IST)'}</p>
        </div>
        <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600" role="group" aria-label="Window">
          {REFUSAL_WINDOWS.map((w) => (
            <button key={w} type="button" onClick={() => setDays(w)} aria-pressed={days === w}
              className={`px-3 py-1 text-sm ${days === w ? 'bg-indigo-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}>
              {w} days
            </button>
          ))}
        </div>
      </div>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Refused" value={t.refused} hint="Not connected" />
          <Stat label="Would refuse" value={t.wouldRefuse} hint="Connected; refused once enforced" />
          <Stat label="Capped" value={t.capped} hint="Connected with a time limit" />
          <Stat label="Would cap" value={t.wouldCap} hint="Connected; capped once enforced" />
        </div>
      )}
      {data && data.byLookup.length > 1 && (
        <p className={muted}>
          {data.byLookup.map((l) => `${LOOKUP_LABEL[l.lookup] || l.lookup}: ${totalOf(l.counts)}`).join(' · ')}
        </p>
      )}
      {daysShown.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" aria-label="Refused calls by day">
              <CountHead first="Day (IST)" />
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {daysShown.map((d) => (
                  <tr key={d.day}><td className={td}>{d.day}</td><CountCells c={d.counts} /></tr>
                ))}
              </tbody>
            </table>
          </div>
          {orgs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" aria-label="Refused calls by organization">
                <CountHead first="Organization" />
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {orgs.map((o) => (
                    <tr key={o.organizationId || 'none'}><td className={td}>{o.organizationName}</td><CountCells c={o.counts} /></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default RefusalCountsPanel;
